import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { TrendingUp, Info, ArrowRight } from "lucide-react";

/**
 * EmployerProgressionCard — ranks UK graduate employers by median
 * 3-year → 5-year salary lift, derived by rolling up HESA LEO
 * medians of every degree that lists a given employer.
 *
 * The point families miss on standard careers pages: the firms that
 * hire the most grads aren't always the ones whose grads grow the
 * fastest. This card surfaces the "accelerator" set — Magic Circle
 * law, MBB, top banks — so a family can weigh short-term prestige vs
 * long-term progression when they're staring at an offer.
 *
 * `variant="compact"` renders a slimmer version for the homepage
 * trust strip — no headline paragraph, no method footer, just the
 * ranked rows so the section fits above the fold.
 *
 * Data source: /api/employers/progression (public, no auth).
 */
export default function EmployerProgressionCard({
  limit = 12,
  minAppearances = 3,
  testid = "employer-progression-card",
  variant = "full",
  country = "UK",
  showCountryTabs = false,
  onCountryChange,
  search = "",
  sortBy = "lift",
  dense = false,
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const compact = variant === "compact";
  // UK + US carry real salary-progression data (LEO / editorial
  // per-degree). The other 6 tier-A markets only carry feeder-
  // density data; the card auto-flips between the two data
  // sources so callers don't need to know which endpoint to hit.
  const isProgressionCountry = country === "UK" || country === "US";

  useEffect(() => {
    setData(null);
    setErr(false);
    const endpoint = isProgressionCountry
      ? `/employers/progression?limit=${limit}&min_appearances=${minAppearances}&country=${country}`
      : `/employers/by-country?limit=${limit}&min_appearances=${minAppearances}&country=${country}`;
    api
      .get(endpoint)
      .then((r) => setData(r.data))
      .catch(() => setErr(true));
  }, [limit, minAppearances, country, isProgressionCountry]);

  if (err) return null;
  if (!data) return null;
  if (!data.employers?.length) return null;

  // Live text filter — kept in the parent so the card also works
  // without a search box (compact strip on Landing, feeder view on
  // course pages). Case-insensitive substring match on the employer
  // name; we don't rank match quality because the base list is
  // already lift-ranked and shortening it destroys that ordering.
  const query = (search || "").trim().toLowerCase();
  let rows = query
    ? data.employers.filter((r) => r.employer.toLowerCase().includes(query))
    : data.employers;

  // Optional NQ-pay sort — re-orders the (already-filtered) rows by
  // the firm-published flagship base annual salary (Trainee/NQ/BA/L3).
  // Rows without a published band sink to the bottom so users still
  // see them, but the top of the ranking becomes a pure "who pays
  // the most day-one" chart, which is a completely different lens
  // to the default "who grows their cohort fastest" (lift) ranking.
  if (sortBy === "nq") {
    rows = [...rows].sort((a, b) => {
      const av = a.flagship_band?.base_annual ?? -1;
      const bv = b.flagship_band?.base_annual ?? -1;
      return bv - av;
    });
  }
  if (rows.length === 0) {
    return (
      <div
        data-testid={`${testid}-empty`}
        className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm text-sm text-stone-500"
      >
        No {data.country_name || data.country} accelerator firms match "<span className="font-mono text-stone-700">{search}</span>".
        Try a shorter search, or clear the filter.
      </div>
    );
  }
  // Lift-ranking mode uses `lift_pct` to draw the bar; density mode
  // uses `n_degrees` as the ranking magnitude. Fall back to 1 to keep
  // the maxLift-driven bar chart safe when all rows are density rows.
  const maxLift = Math.max(...rows.map((r) => r.lift_pct ?? r.n_degrees ?? 1));
  // Currency symbol drives every £/$ label the card renders. Density-
  // mode countries have no salary column so this is unused there.
  const currencySymbol = data.currency === "USD" ? "$" : "£";
  const kfmt = (v) => `${currencySymbol}${Math.round(v / 1000)}k`;

  return (
    <div
      data-testid={testid}
      className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <div className="label-uppercase text-stone-600 flex items-center gap-1.5">
            <TrendingUp className="h-3 w-3 text-emerald-700" />
            Career-accelerator employers
          </div>
          <h2 className="mt-1 font-editorial text-2xl leading-snug">
            What each firm's flagship role actually pays
          </h2>
        </div>
        <div className="text-[10px] font-underground-sc tracking-wider text-stone-400 text-right shrink-0">
          Firm-published band<br />
          + cohort context
        </div>
      </div>

      {/* Country switcher — only rendered when the caller opts in
          (e.g. the dedicated Employers Index page). Compact and
          Landing-strip variants leave this off so the card stays
          slim. UK/US show salary-lift rankings; the other 6 tier-A
          markets fall back to feeder-density rankings. */}
      {showCountryTabs && (
        <div
          className="mt-3 mb-4 flex flex-wrap items-center gap-1.5 text-xs"
          data-testid={`${testid}-country-tabs`}
        >
          {[
            { key: "UK", label: "🇬🇧 UK", hint: "HESA LEO 3y → 5y (HMRC-verified)" },
            { key: "US", label: "🇺🇸 US", hint: "Editorial starting → 3y salary (USD)" },
            { key: "IE", label: "🇮🇪 Ireland",  hint: "Ranked by feeder-course density" },
            { key: "DE", label: "🇩🇪 Germany",  hint: "Ranked by feeder-course density" },
            { key: "FR", label: "🇫🇷 France",   hint: "Ranked by feeder-course density" },
            { key: "IT", label: "🇮🇹 Italy",    hint: "Ranked by feeder-course density" },
            { key: "ES", label: "🇪🇸 Spain",    hint: "Ranked by feeder-course density" },
            { key: "PT", label: "🇵🇹 Portugal", hint: "Ranked by feeder-course density" },
            { key: "AE", label: "🇦🇪 UAE",      hint: "Ranked by feeder-course density" },
          ].map((t) => (
            <button
              key={t.key}
              type="button"
              title={t.hint}
              onClick={() => onCountryChange?.(t.key)}
              data-testid={`${testid}-country-${t.key.toLowerCase()}`}
              aria-pressed={country === t.key}
              className={`px-3 py-1 rounded-full border font-medium transition ${
                country === t.key
                  ? "border-[#4C1D95] bg-[#4C1D95] text-white"
                  : "border-stone-200 bg-white text-stone-700 hover:border-[#4C1D95] hover:text-[#4C1D95]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}
      <p
        className={`text-sm text-stone-600 max-w-2xl mb-4 leading-relaxed ${compact ? "hidden" : ""}`}
      >
        {isProgressionCountry ? (
          <>
            Each row leads with the firm's own <span className="font-medium">published salary band</span>
            {" "}for its flagship early-career role (Trainee / NQ / Business Analyst / L3). The smaller
            {" "}{data.currency} number beside it is the LEO cohort figure — the median earnings of the whole
            {" "}graduating class of feeder courses,{" "}
            <span className="font-medium">not this firm's pay</span> — useful as an accelerator signal but not
            {" "}what you'd actually earn at the firm.
          </>
        ) : (
          <>
            Each firm below is ranked by <span className="font-medium">how many curated {data.country_name} degrees</span> list
            {" "}it as a top employer — the honest breadth signal for markets where cohort-level salary
            {" "}progression data isn't published centrally. Tap any firm to see its regional hub blurb
            {" "}and grad-scheme link.
          </>
        )}
      </p>

      <ol className="divide-y divide-stone-100">
        {rows.map((r, i) => {
          const href = r.slug
            ? `/employers/${r.slug}${country && country !== "UK" ? `?country=${country}` : ""}`
            : null;
          const NameEl = href ? Link : "span";
          const nameProps = href
            ? { to: href, "data-testid": `${testid}-row-${i}-link`,
                className: "font-medium text-stone-900 truncate block hover:text-[#4C1D95] transition-colors group" }
            : { className: "font-medium text-stone-900 truncate block" };
          return (
          <li
            key={r.employer}
            data-testid={`${testid}-row-${i}`}
            className={`${dense ? "py-1.5" : "py-2.5"} flex items-center gap-4`}
          >
            <span className="w-6 shrink-0 text-right text-[11px] font-mono text-stone-400 tabular-nums">
              {i + 1}
            </span>
            <span className="flex-1 min-w-0">
              <NameEl {...nameProps}>
                {r.employer}
                {r.flagship_band && (
                  <span
                    className="ml-2 inline-flex items-center rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 px-1.5 py-0.5 text-[10px] font-mono font-semibold tabular-nums align-middle"
                    title={`${r.flagship_band.role} · firm-published NQ pay`}
                    data-testid={`${testid}-row-${i}-pay-chip`}
                  >
                    {r.flagship_band.currency === "USD" ? "$" : "£"}
                    {Math.round(r.flagship_band.base_annual / 1000)}k
                  </span>
                )}
                {r.lift_pct != null && r.lift_pct > 0 && (
                  <span
                    className="ml-1 inline-flex items-center rounded-full bg-[#F5F0FF] border border-[#4C1D95]/25 text-[#4C1D95] px-1.5 py-0.5 text-[10px] font-mono font-semibold tabular-nums align-middle"
                    title={`Feeder-cohort LEO 3yr → 5yr median earnings growth (+${r.lift_pct}%)`}
                    data-testid={`${testid}-row-${i}-lift-chip`}
                  >
                    +{r.lift_pct}% 5y
                  </span>
                )}
                {href && (
                  <ArrowRight
                    className="inline-block h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                    aria-hidden
                  />
                )}
              </NameEl>
              {!dense && (
                <span className="text-[10px] font-underground-sc tracking-wider text-stone-500">
                  across {r.n_degrees} {data.country_name || data.country} {r.n_degrees === 1 ? "degree" : "degrees"}
                  {r.n_unis > 0 && !isProgressionCountry && (
                    <span className="ml-1.5 text-stone-500/80">
                      · {r.n_unis} {r.n_unis === 1 ? "uni" : "unis"}
                    </span>
                  )}
                  {r.flagship_band && r.lift_pct != null && (
                    <span className="ml-1.5 text-emerald-700/70">
                      · cohort +{r.lift_pct}% ({kfmt(r.leo3_p50_gbp)}→{kfmt(r.leo5_p50_gbp)})
                    </span>
                  )}
                </span>
              )}
            </span>
            <span className={`${dense ? "hidden" : "hidden sm:flex"} items-center gap-3 shrink-0 font-mono text-[11px] text-stone-500`}>
              {r.flagship_band ? (
                <span
                  className="flex items-baseline gap-2"
                  data-testid={`${testid}-row-${i}-flagship`}
                  title={`${r.flagship_band.role} · firm-published band`}
                >
                  <span className="text-stone-900 font-semibold text-sm tabular-nums">
                    {r.flagship_band.currency === "USD" ? "$" : "£"}
                    {Math.round(r.flagship_band.base_annual / 1000)}k
                  </span>
                  <span className="text-[10px] text-stone-500 uppercase tracking-wider">
                    {r.flagship_band.role}
                  </span>
                </span>
              ) : isProgressionCountry ? (
                <>
                  <span data-testid={`${testid}-row-${i}-leo3`}>
                    {kfmt(r.leo3_p50_gbp)}
                  </span>
                  <span aria-hidden className="text-stone-400">→</span>
                  <span data-testid={`${testid}-row-${i}-leo5`} className="text-stone-800 font-semibold">
                    {kfmt(r.leo5_p50_gbp)}
                  </span>
                  <span className="text-[10px] uppercase tracking-wider text-stone-400">cohort</span>
                </>
              ) : (
                <span
                  data-testid={`${testid}-row-${i}-density`}
                  className="flex items-baseline gap-2"
                  title={`Appears in ${r.n_degrees} curated ${data.country_name} degrees across ${r.n_unis} unis`}
                >
                  <span className="text-stone-900 font-semibold text-sm tabular-nums">
                    {r.n_degrees}
                  </span>
                  <span className="text-[10px] text-stone-500 uppercase tracking-wider">
                    degrees · {r.n_unis} unis
                  </span>
                </span>
              )}
            </span>
            <span className={`${dense ? "hidden sm:inline-block" : ""} w-28 sm:w-40 shrink-0`}>
              <div className="relative h-2 rounded-full bg-stone-100 overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 rounded-full ${isProgressionCountry ? "bg-emerald-500" : "bg-[#4C1D95]"}`}
                  style={{ width: `${Math.max(6, ((r.lift_pct ?? r.n_degrees) / maxLift) * 100)}%` }}
                />
              </div>
            </span>
            <span
              className={`w-14 shrink-0 text-right font-mono text-sm font-semibold tabular-nums ${isProgressionCountry ? "text-emerald-700" : "text-[#4C1D95]"}`}
              data-testid={`${testid}-row-${i}-lift`}
            >
              {isProgressionCountry ? `+${r.lift_pct}%` : `×${r.n_degrees}`}
            </span>
          </li>
          );
        })}
      </ol>

      <div className={`mt-4 flex items-start gap-2 text-[11px] text-stone-500 leading-relaxed ${compact ? "hidden" : ""}`}>
        <Info className="h-3 w-3 shrink-0 mt-0.5" />
        <span>
          {isProgressionCountry ? (
            <>
              Method: for each employer we compute the median cohort{" "}
              {data.early_label.toLowerCase()} and {data.later_label.toLowerCase()}{" "}
              across all {data.country} degrees listing them, requiring ≥{" "}
              {data.min_appearances} appearances so one-off names don't distort
              the chart. These are <span className="font-medium">whole-cohort</span>{" "}
              earnings, not per-firm pay bands. Source: {data.progression_source}.
            </>
          ) : (
            <>
              Method: ranked by number of curated {data.country_name} undergraduate
              degrees each firm appears in as a top employer (≥ {data.min_appearances}{" "}
              appearances). No salary progression figures — cohort-level payroll
              data isn't published centrally for {data.country_name}. Source:
              WhichUniversityAdvisor curated firm hubs + degree-level top-employer
              rows.
            </>
          )}
        </span>
      </div>
      {/* Compact-mode gets a single-line CTA instead — routes users to
          the dedicated /employers index where the full ranked list +
          country switcher live, keeping the homepage strip lean. */}
      {compact && (
        <div className="mt-4 text-right">
          <Link
            to="/employers"
            data-testid={`${testid}-compact-cta`}
            className="inline-flex items-center gap-1 text-xs text-[#4C1D95] hover:underline"
          >
            See all {data.total_employers} accelerator firms
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      )}
    </div>
  );
}
